import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  createPartnerVignetteRange,
  getSalesforceVignetteRangeInfo,
  getSalesforceVignetteStatusesEnums,
  updatePartnerVignetteRange,
} from './queries';
import { cloneDeep } from 'lodash';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { isEmpty } from '@utils/validationUtils';
import { getError } from '@utils/graph-utils';
import { VignetteLookupsToList, graphqlToVignetteRangeInfo } from './utils';
import { useParams } from 'react-router-dom';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';

const VignetteRangeDrawer: React.FC<IVignetteRangeDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  vignetteRangeId,
}) => {
  const params = useParams();

  const [booted, setBooted] = useState<boolean>(false);

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  const [vignetteRangeAction] = useMutation(
    vignetteRangeId
      ? updatePartnerVignetteRange()
      : createPartnerVignetteRange()
  );

  const [vignetteRangeInfoQuery] = useLazyQuery(
    getSalesforceVignetteRangeInfo()
  );

  const [vignetteStatusesEnumsResults] = useLazyQuery(
    getSalesforceVignetteStatusesEnums()
  );

  const loadLovList = async () => {
    const result = await vignetteStatusesEnumsResults();
    const list = VignetteLookupsToList(result.data);
    return list;
  };

  const getEntityInfo = async () => {
    let vignetteRangeInfo: IVignetteRangeInfo = {
      rangeCode: '',
      rangeYear: null,
      rangeFrom: null,
      rangeTo: null,
      rangeStatus: 'ACTIVE',
    };

    if (!isEmpty(vignetteRangeId)) {
      const apiResult = await vignetteRangeInfoQuery({
        variables: { id: vignetteRangeId },
      });
      if (apiResult.data) {
        vignetteRangeInfo = graphqlToVignetteRangeInfo(apiResult?.data);
      }
    }

    return vignetteRangeInfo;
  };

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      const updatedSections = cloneDeep(inputsForm);
      const updatedInputs = updatedSections[0].inputs;

      const [entityData, lovData] = await Promise.all([
        getEntityInfo(),
        loadLovList(),
      ]);

      (updatedInputs.rangeStatus as ISelectFormFieldProps).selectOptions =
        lovData['SalesforceManagement_VignetteRangeStatuses'];
      if (entityData) {
        updatedInputs.rangeYear.value = entityData.rangeYear;
        updatedInputs.rangeCode.value = entityData.rangeCode;
        updatedInputs.rangeFrom.value = entityData.rangeFrom;
        updatedInputs.rangeTo.value = entityData.rangeTo;
        updatedInputs.rangeStatus.value = entityData.rangeStatus;
        setInputsForm(updatedSections);
      }
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(drawerSections, values);
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variables = {
        entityId: params.id,
        rangeCode: data.rangeCode,
        rangeFrom: parseInt(data.rangeFrom as unknown as string),
        rangeStatus: data.rangeStatus,
        rangeTo: parseInt(data.rangeTo as unknown as string),
        rangeYear: parseInt(data.rangeYear as unknown as string),
      };

      const res = await vignetteRangeAction({
        variables: vignetteRangeId
          ? {
              ...variables,
              selectedBusinessPartnerVignetteRangeId: vignetteRangeId,
            }
          : variables,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {vignetteRangeId
              ? 'Vignette Range successfully updated'
              : 'Vignette Range successfully created'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          setFormDisabled(false);
          onSuccess();
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (err) {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormDrawer
      title={vignetteRangeId ? 'Modify Vignette Range' : 'New Vignette Range'}
      onClose={() => onClose()}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default VignetteRangeDrawer;
